import { useMemo } from 'react';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { EventPricingTier } from 'legacy/common/types/models';
import { EventTierCreation, TierReorderDto } from 'legacy/events/types';

import Ticket from './components/Ticket';
import classNames from 'classnames';
import { useEventEditorContext } from 'legacy/events/contexts/EventEditorContext';
import { OutgoingEventProductDto } from 'api/dtos/events/products';

type TicketsProps = {
  eventId: string;
  eventShareToken: string;
  tickets: Array<OutgoingEventProductDto>;
  selectedTicket: OutgoingEventProductDto;
  selectedIndex: number;
  toggleTicketTier: (id: string, isDisabled: boolean) => void;
  toggleHideTier: (id: string) => void;
  setSelectedIndex: (id: string) => void;
  setSelectedTicket: (ticket: OutgoingEventProductDto) => void;
  isSortable?: boolean;
  sectionId?: string;
  onReorder?: (tickets: Array<TierReorderDto>) => void;
};

const Tickets: React.FC<TicketsProps> = ({
  eventId,
  eventShareToken,
  tickets,
  isSortable,
  toggleTicketTier,
  toggleHideTier,
  setSelectedIndex,
  setSelectedTicket,
  sectionId
}) => {
  const { t } = useTranslation(['events']);
  const { tierScansData } = useEventEditorContext();

  // Sort tickets by sortIndex
  const sortedTickets = useMemo(
    () => tickets.sort((a, b) => a.sortIndex - b.sortIndex),
    [tickets]
  );

  if (!isSortable) {
    return (
      <div className="w-full py-6 pb-10">
        <div className="flex flex-col gap-4">
          {sortedTickets.map((ticket: any) => (
            <div key={ticket.id} data-ticket-id={ticket.id}>
              <Ticket
                eventId={eventId}
                eventShareToken={eventShareToken}
                ticket={ticket}
                toggleTicketTier={(isDisabled: boolean) => {
                  toggleTicketTier(ticket.id, isDisabled);
                }}
                onClick={() => {
                  setSelectedTicket(ticket);
                  setSelectedIndex(ticket.id);
                }}
                isSortable={isSortable}
                toggleHideTier={() =>
                  toggleHideTier(
                    (ticket as EventPricingTier)?._id ||
                      (ticket as EventTierCreation).id
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Droppable droppableId={sectionId} type="tiers">
        {(providedTiers, snapshot) => (
          <div
            className={classNames(
              'mx-2 my-6 py-2 max-w-[550px] rounded-2xl border-2 border-dashed border-transparent bg-[#FDFDFD] transition-all duration-200 ease-in',
              {
                'bg-[#EFF2FF]! border-[#6585FF]!': snapshot.isDraggingOver
              }
            )}
            ref={providedTiers.innerRef}
            data-rfd-droppable-context-id={
              providedTiers.droppableProps['data-rfd-droppable-context-id']
            }
            data-rfd-droppable-id={
              providedTiers.droppableProps['data-rfd-droppable-id']
            }
          >
            <div className="flex flex-col gap-4">
              {sortedTickets.map((ticket: any, index: number) => (
                <Draggable
                  index={index}
                  key={ticket.id}
                  draggableId={ticket.id}
                >
                  {(provided, ticketSnapshot) => (
                    <div
                      ref={provided.innerRef}
                      data-ticket-id={ticket.id}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...provided.draggableProps}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...provided.dragHandleProps}
                      className={classNames(
                        'relative transition-[margin] p-0 duration-200 ease-in',
                        {
                          'bg-white rounded-2xl shadow-[-3px_14px_19.3px_0px_rgba(32,32,32,0.1) border-2 border-[#6585FF]':
                            ticketSnapshot.isDragging,
                          'bg-white rounded-2xl': !ticketSnapshot.isDragging,
                          'mx-2!':
                            snapshot.isDraggingOver &&
                            !ticketSnapshot.draggingOver
                        }
                      )}
                    >
                      <Ticket
                        eventId={eventId}
                        eventShareToken={eventShareToken}
                        ticket={ticket}
                        tierScansData={tierScansData?.data?.find(
                          (tier) => tier.tierId === ticket.id
                        )}
                        toggleTicketTier={(isDisabled: boolean) => {
                          toggleTicketTier(ticket.id, isDisabled);
                        }}
                        toggleHideTier={() =>
                          toggleHideTier(
                            (ticket as EventPricingTier)?._id ||
                              (ticket as EventTierCreation).id
                          )
                        }
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setSelectedIndex(ticket.id);
                        }}
                        isSortable={isSortable}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
            </div>

            {sortedTickets?.length === 0 ? (
              <div
                className={classNames(
                  'flex items-center justify-center py-32 -my-2 border-2 border-dashed rounded-2xl bg-[#FDFDFD] transition-colors duration-200 ease-in',
                  {
                    'bg-[#EFF2FF]! border-transparent': snapshot.isDraggingOver
                  }
                )}
              >
                <p className="text-[#575757]">
                  {t('events:pricingTiers.categories.dragAndDropTiers')}
                </p>
              </div>
            ) : (
              providedTiers.placeholder
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default Tickets;
